/**
 * Alert Bar — the promotional banner at the top of the header.
 *
 * Structure (matches production layout):
 * 1. Site-switcher tabs (FSAstore.com / HSAstore.com): absolutely positioned
 *    over the top-left of the alert banner. The banner's own background color
 *    (set by Builder) provides the strip color; the active tab is a white rectangle;
 *    the inactive tab is the same but with a dark grey background.
 *    Legacy `tabbed-sites` Content Asset; see SFCC-10633.
 * 2. Banner content: fetched from Builder "alert-bar-workers" model.
 *    TPA override: if user has afid cookie, swaps to TPA-specific banner.
 *
 * COM-197: below `lg` the tabs render ONLY on the homepage. The winning AB test
 * (HSA_HideFSAHSATabsMobile_02232026) found tab usage is ~4.7% of sessions and
 * overwhelmingly from the homepage, so on every other page the strip was
 * spending mobile vertical space for almost no clicks; removing it improved
 * ATC%. Desktop is unchanged — the `lg:` layout still shows them everywhere.
 */
import { memo, useMemo } from "react";
import { useLocation } from "react-router";

import { VisibleChromeContent } from "~/hec/builder/visible-chrome-content";
import { useBuilder } from "~/hec/hooks/useBuilder";
import { sanitizeHtml } from "~/utils/sanitize-html";

import { SITE_TABS, deriveSiblingHref } from "./sibling-host";

/**
 * The bar's Builder content, memoised (COM-197 review). `AlertBar` itself
 * re-renders on every navigation, filter clicks included, and memoising it would
 * not change that: `useBuilder()` reads `useRouteLoaderData("root")`, which
 * subscribes to React Router's state context, and `AppLayout` (`useLocation()`)
 * re-renders the whole header above it anyway. What is worth skipping is the
 * Builder subtree, and its props are stable across navigations: `model` and
 * `apiKey` are strings, and `content` keeps its reference because the layout
 * loader's `shouldRevalidate` skips cross-page GETs. A real layout revalidation
 * (sign-in, basket POST) hands over a new object, so fresh content still renders.
 */
const MemoVisibleChromeContent = memo(VisibleChromeContent);

interface AlertBarProps {
  /** Pre-fetched alert bar content from the layout loader (SSR) */
  alertBarContent?: any;
  /** Pre-fetched TPA banner content (if user has TPA cookie) */
  tpaBannerContent?: string | null;
  /** The three-letter site code (hsa, fsa, wdh) */
  siteCode?: string;
  /**
   * Current request host (may include a port). Used to point the site-switcher
   * sibling tab at the same deployment tier. Omitted / prod -> the hardcoded
   * production URL is kept.
   */
  hostname?: string;
}

export function AlertBar({
  alertBarContent,
  tpaBannerContent,
  siteCode = "hsa",
  hostname,
}: AlertBarProps) {
  const config = useBuilder();
  // Same reasoning as `MemoVisibleChromeContent`: the banner string is stable
  // across navigations, so run the sanitiser's regex pass once, not per render.
  const tpaBannerHtml = useMemo(
    () =>
      tpaBannerContent
        ? // allowStyle: the TPA banner ships bespoke per-affiliate CSS in a
          // <style> block that is load-bearing (logo sizing, colors, responsive
          // layout). Same trust level as the standard alert-bar Builder content;
          // script/iframe/handlers are still stripped. SFCC-11087.
          sanitizeHtml(tpaBannerContent, { allowStyle: true })
        : null,
    [tpaBannerContent],
  );
  // On non-prod tiers (devhec / per-site preview Workers / localhost) point the
  // sibling tab at the same-tier sibling host so QA/devs stay on their
  // deployment level. On prod/staging `deriveSiblingHref` returns null and the
  // hardcoded production URL (with utm tracking) is kept. Freeform item F14.
  // The remapped href keeps the production utm params (COM-661 QA).
  const tabs = (SITE_TABS[siteCode] || SITE_TABS.hsa).map(tab => {
    if (tab.current || !tab.code) return tab;
    const sibling = deriveSiblingHref(
      hostname,
      tab.code,
      tab.href.startsWith("http") ? new URL(tab.href).search : "",
    );
    return sibling ? { ...tab, href: sibling } : tab;
  });

  if (tpaBannerHtml !== null) {
    return (
      <div className="relative bg-gray-01 text-center">
        <SiteTabs tabs={tabs} />
        <div
          className="py-2 text-center text-sm"
          dangerouslySetInnerHTML={{ __html: tpaBannerHtml }}
        />
      </div>
    );
  }

  return (
    <div className="relative lg:h-[35px] lg:[&_.builder-blocks>div]:h-[35px]">
      <SiteTabs tabs={tabs} />
      {/* SFCC-11742: render only when the loader resolved an in-window entry.
          When Builder scheduling filters the bar out (no entry inside its
          start/end window), `alertBarContent` is null — gate here so a
          "scheduled OFF" window stays off and never falls through to an
          unscheduled client-side `<Content>` fetch. Mirrors the legacy
          composable Alert Bar's `alertBarContent && …` guard. */}
      {alertBarContent ? (
        <MemoVisibleChromeContent
          model="alert-bar-workers"
          content={alertBarContent}
          apiKey={config.config.api_key}
        />
      ) : null}
    </div>
  );
}

/**
 * Site-switcher tabs (FSAstore.com / HSAstore.com).
 *
 * Absolutely positioned over the top-left of the alert banner. Both tabs are
 * flat rectangles full strip-height (no border-radius); the active tab is
 * `#fff` and the inactive tab is `gray-02` (`#ccc`). Text is dark blue
 * (`#375481`) on both.
 *
 * Colors and typography mirror the legacy `tabbed-sites` Content Asset
 * (org_hec library; see `migrations/20221220T0535_fsa_hsa_tabbed_experience`).
 */
function SiteTabs({
  tabs,
}: {
  tabs: { label: string; href: string; current: boolean }[];
}) {
  // COM-197: homepage is the only route that keeps the tabs below `lg` (984px).
  // Exact match on "/" — the home route is `index("./routes/index.tsx")`, and a
  // query string (the site-switcher's own utm params, TPA landing params) must
  // not change the answer, so compare `pathname` rather than the full URL.
  // Read here rather than in AlertBar so the route subscription stays on the
  // tabs and not on the bar's Builder content.
  const hideOnMobile = useLocation().pathname !== "/";
  // CSS, not a conditional render: the markup is identical on server and
  // client, so there is no hydration mismatch and no viewport probe. `hidden`
  // collapses the 24px strip entirely, which is the mobile vertical space the
  // AB test was reclaiming; `lg:flex` restores the desktop layout, where the
  // row is absolutely positioned and out of flow anyway.
  const display = hideOnMobile ? "hidden lg:flex" : "flex";
  return (
    <div
      className={`relative z-10 ${display} h-6 w-full items-stretch lg:absolute lg:left-5 lg:right-auto lg:top-0 lg:h-full lg:w-auto`}
    >
      {tabs.map(tab => {
        const baseClasses =
          "inline-flex flex-1 items-center justify-center px-2.5 font-grotesque text-[14px] font-light leading-none text-[#375481] lg:flex-none lg:justify-start";
        if (tab.current) {
          return (
            <span
              key={tab.label}
              className={`${baseClasses} cursor-default bg-white`}
            >
              {tab.label}
            </span>
          );
        }
        return (
          <a
            key={tab.label}
            href={tab.href}
            // COM-661: marks the cross-store tab so the TPA transfer listener
            // can find it. The href is NOT decorated here — see
            // `TpaSiteSwitchTracker` for why it has to happen at click time.
            data-tpa-site-switch=""
            className={`${baseClasses} bg-gray-02 hover:no-underline`}
            target={tab.href.startsWith("http") ? "_blank" : undefined}
            rel={
              tab.href.startsWith("http") ? "noopener noreferrer" : undefined
            }
          >
            {tab.label}
          </a>
        );
      })}
    </div>
  );
}
